import React, { useEffect, useState } from 'react';
import { Header } from '../components/Header';
import { Footer } from '../components/Footer';
import { motion } from 'framer-motion';
import { PlusIcon, XIcon, InfoIcon, TrendingDownIcon, CheckCircleIcon, RefreshCwIcon, AlertTriangleIcon, StarIcon } from 'lucide-react';
type Commerce7Plan = 'basic' | 'pro' | 'experience' | 'enterprise' | 'none';
type ShopifyPlan = 'basic' | 'grow' | 'advanced' | 'plus' | 'none';
type POSTerminal = 'shopify' | 'own';
type WinePikPlan = 'none' | 'starter' | 'growth' | 'pro';
type WineHubPlan = 'none' | 'club_lite' | 'club_unlimited' | 'pro_scaler' | 'pro_unlimited' | 'enterprise';
type AwtomicPlan = 'none' | 'professional' | 'enterprise';
interface CustomApp {
  id: string;
  name: string;
  costUSD: number;
}
interface Commerce7PlanConfig {
  value: Commerce7Plan;
  label: string;
  priceAUD: number;
  txFee: number;
  revenueRange: string;
  custom?: boolean;
}
interface ShopifyPlanConfig {
  value: ShopifyPlan;
  label: string;
  priceAUD: number;
  ccRate: number;
  ccFixed: number;
  thirdPartyFee: number;
  inPersonRate: number;
}
interface AppPlanConfig {
  value: string;
  label: string;
  priceUSD: number;
  note?: string;
  custom?: boolean;
  revenueFeeRate?: number;
  revenueThresholdUSD?: number;
}
const C7_PLANS: Commerce7PlanConfig[] = [{
  value: 'basic',
  label: 'Commerce7 Basic',
  priceAUD: 149,
  txFee: 1.5,
  revenueRange: 'Up to $600k annual DTC revenue'
}, {
  value: 'pro',
  label: 'Commerce7 Pro',
  priceAUD: 399,
  txFee: 1.0,
  revenueRange: 'Up to $4M annual DTC revenue'
}, {
  value: 'experience',
  label: 'Commerce7 Experience',
  priceAUD: 1249,
  txFee: 0.75,
  revenueRange: '$4M–$6M annual DTC revenue'
}, {
  value: 'enterprise',
  label: 'Commerce7 Enterprise',
  priceAUD: 0,
  txFee: 0,
  revenueRange: 'Over $6M annual DTC revenue',
  custom: true
}, {
  value: 'none',
  label: 'None / Not applicable',
  priceAUD: 0,
  txFee: 0,
  revenueRange: ''
}];
const SHOPIFY_PLANS: ShopifyPlanConfig[] = [{
  value: 'basic',
  label: 'Basic',
  priceAUD: 56,
  ccRate: 1.75,
  ccFixed: 0.3,
  thirdPartyFee: 2.0,
  inPersonRate: 1.95
}, {
  value: 'grow',
  label: 'Grow',
  priceAUD: 149,
  ccRate: 1.6,
  ccFixed: 0.3,
  thirdPartyFee: 1.0,
  inPersonRate: 1.85
}, {
  value: 'advanced',
  label: 'Advanced',
  priceAUD: 575,
  ccRate: 1.4,
  ccFixed: 0.3,
  thirdPartyFee: 0.6,
  inPersonRate: 1.75
}, {
  value: 'plus',
  label: 'Plus',
  priceAUD: 3700,
  ccRate: 1.2,
  ccFixed: 0.3,
  thirdPartyFee: 0.2,
  inPersonRate: 1.6
}, {
  value: 'none',
  label: 'None / Not applicable',
  priceAUD: 0,
  ccRate: 0,
  ccFixed: 0,
  thirdPartyFee: 0,
  inPersonRate: 0
}];
const WINEPIK_PLANS: AppPlanConfig[] = [{
  value: 'none',
  label: 'None / Not applicable',
  priceUSD: 0
}, {
  value: 'starter',
  label: 'WinePik Starter',
  priceUSD: 99,
  note: 'Up to $2,500 USD/mo sub revenue. No usage fees.',
  revenueFeeRate: 0,
  revenueThresholdUSD: 2500
}, {
  value: 'growth',
  label: 'WinePik Growth',
  priceUSD: 299,
  note: 'First $5,000 USD/mo included. 1.75% above threshold.',
  revenueFeeRate: 1.75,
  revenueThresholdUSD: 5000
}, {
  value: 'pro',
  label: 'WinePik Pro',
  priceUSD: 449,
  note: 'First $10,000 USD/mo included. 1.25% above threshold.',
  revenueFeeRate: 1.25,
  revenueThresholdUSD: 10000
}];
const WINEHUB_PLANS: AppPlanConfig[] = [{
  value: 'none',
  label: 'None / Not applicable',
  priceUSD: 0
}, {
  value: 'club_lite',
  label: 'WineHub Club Lite',
  priceUSD: 199,
  note: 'Up to 500 subscribers. 2% managed revenue fee.',
  revenueFeeRate: 2.0
}, {
  value: 'club_unlimited',
  label: 'WineHub Club Unlimited',
  priceUSD: 279,
  note: 'Unlimited subscribers. 2% managed revenue fee.',
  revenueFeeRate: 2.0
}, {
  value: 'pro_scaler',
  label: 'WineHub Pro Scaler',
  priceUSD: 299,
  note: 'Up to 1,000 club members. 2% managed revenue fee.',
  revenueFeeRate: 2.0
}, {
  value: 'pro_unlimited',
  label: 'WineHub Pro Unlimited',
  priceUSD: 499,
  note: 'Unlimited subscribers. 1.75% managed revenue fee.',
  revenueFeeRate: 1.75
}, {
  value: 'enterprise',
  label: 'WineHub Enterprise',
  priceUSD: 0,
  note: 'Custom pricing.',
  custom: true
}];
const AWTOMIC_PLANS: AppPlanConfig[] = [{
  value: 'none',
  label: 'None / Not applicable',
  priceUSD: 0
}, {
  value: 'professional',
  label: 'Awtomic Professional',
  priceUSD: 299,
  note: '+1% on subscription revenue. Yearly discount available.',
  revenueFeeRate: 1.0
}, {
  value: 'enterprise',
  label: 'Awtomic Enterprise',
  priceUSD: 0,
  note: 'Volume based pricing.',
  custom: true
}];
function getRecommendedWinePikPlan(subRevenueUSD: number): WinePikPlan {
  if (subRevenueUSD <= 2500) return 'starter';
  if (subRevenueUSD <= 22500) return 'growth';
  return 'pro';
}
function calcWinePikCostUSD(plan: AppPlanConfig, subRevenueUSD: number): number {
  if (plan.value === 'none' || plan.custom) return 0;
  const overage = plan.revenueThresholdUSD ? Math.max(0, subRevenueUSD - plan.revenueThresholdUSD) : subRevenueUSD;
  const revFee = plan.revenueFeeRate ? overage * plan.revenueFeeRate / 100 : 0;
  return plan.priceUSD + revFee;
}
function calcRevenueFeeUSD(plan: AppPlanConfig, subRevenueUSD: number): number {
  if (!plan.revenueFeeRate || plan.revenueFeeRate === 0 || plan.custom) return 0;
  const taxable = plan.revenueThresholdUSD ? Math.max(0, subRevenueUSD - plan.revenueThresholdUSD) : subRevenueUSD;
  return taxable * plan.revenueFeeRate / 100;
}
const STRIPE_RATE = 1.7;
const STRIPE_FIXED = 0.3;
const LEGACY_DECAY = [1.0, 0.7, 0.4, 0.15];
const LEGACY_DECAY_LABELS = ['Now', 'Year 1', 'Year 2', 'Year 3'];
const FALLBACK_RATE = 1.57;
const KLAVIYO_TIERS = [{
  max: 500,
  usd: 0
}, {
  max: 1000,
  usd: 20
}, {
  max: 5000,
  usd: 45
}, {
  max: 10000,
  usd: 100
}, {
  max: 25000,
  usd: 175
}];
function getKlaviyoCostUSD(contacts: number): number {
  for (const tier of KLAVIYO_TIERS) {
    if (contacts <= tier.max) return tier.usd;
  }
  return -1;
}
function getKlaviyoTierLabel(contacts: number, rate: number): string {
  if (contacts <= 500) return 'Free';
  for (const tier of KLAVIYO_TIERS) {
    if (contacts <= tier.max && tier.usd > 0) return `USD $${tier.usd}/mo (~AUD $${Math.round(tier.usd * rate)}/mo)`;
  }
  return 'Contact Klaviyo for pricing';
}
const fadeIn = {
  initial: {
    opacity: 0,
    y: 20
  },
  whileInView: {
    opacity: 1,
    y: 0
  },
  viewport: {
    once: true
  },
  transition: {
    duration: 0.5
  }
};
const inputClass = 'w-full px-4 py-3 border border-[#D4A57440] rounded-lg focus:outline-none focus:ring-2 focus:ring-[#5F6F52] focus:border-transparent transition-all bg-white text-[#2C2416]';
const cardBase = 'w-full text-left p-5 rounded-xl border-2 transition-all duration-300';
const cardSelected = 'border-[#5F6F52] bg-[#5F6F52]/5 shadow-md';
const cardUnselected = 'border-[#D4A57420] bg-white hover:border-[#5F6F52]/40';
function RadioDot({
  selected
}: {
  selected: boolean;
}) {
  return <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center transition-all flex-shrink-0 ${selected ? 'border-[#5F6F52] bg-[#5F6F52]' : 'border-[#D4A57440]'}`} data-id="element-1030">
      {selected && <div className="w-2 h-2 bg-white rounded-full" data-id="element-1031" />}
    </div>;
}
function Toggle({
  value,
  onChange,
  label
}: {
  value: boolean;
  onChange: (v: boolean) => void;
  label: string;
}) {
  return <button onClick={() => onChange(!value)} className="flex items-center gap-3 text-left" data-id="element-1032">
      <div className={`w-12 h-6 rounded-full transition-all duration-300 relative flex-shrink-0 ${value ? 'bg-[#5F6F52]' : 'bg-[#D4A57440]'}`} data-id="element-1033">
        <div className={`w-5 h-5 bg-white rounded-full absolute top-0.5 transition-all duration-300 shadow-sm ${value ? 'left-[26px]' : 'left-0.5'}`} data-id="element-1034" />
      </div>
      <span className="text-[#2C2416] text-sm font-medium" data-id="element-1035">{label}</span>
    </button>;
}
interface AppPlanSelectorProps {
  label: string;
  category: string;
  plans: AppPlanConfig[];
  selected: string;
  onSelect: (v: string) => void;
  rate: number;
  recommendedPlan?: string;
  subRevenueUSD?: number;
}
function AppPlanSelector({
  label,
  category,
  plans,
  selected,
  onSelect,
  rate,
  recommendedPlan,
  subRevenueUSD
}: AppPlanSelectorProps) {
  return <div data-id="element-1036">
      <p className="text-[#8B7355] text-xs font-semibold tracking-[1.5px] uppercase mb-2" data-id="element-1037">{category}</p>
      <p className="text-[#2C2416] font-medium mb-3" data-id="element-1038">{label}</p>
      <div className="space-y-2" data-id="element-1039">
        {plans.map(plan => {
        const isRecommended = recommendedPlan && plan.value === recommendedPlan && plan.value !== 'none';
        return <button key={plan.value} onClick={() => onSelect(plan.value)} className={`${cardBase} ${selected === plan.value ? cardSelected : cardUnselected} relative`} data-id="element-1040">
              {isRecommended && <div className="absolute -top-2.5 left-4 flex items-center gap-1 bg-[#8B7355] text-white text-xs font-semibold px-2.5 py-0.5 rounded-full" data-id="element-1041">
                  <StarIcon className="w-3 h-3" data-id="element-1042" />Best for your volume
                </div>}
              <div className="flex items-start justify-between gap-4" data-id="element-1043">
                <div className="flex-1 min-w-0" data-id="element-1044">
                  <span className="text-[#2C2416] font-medium block text-sm" data-id="element-1045">{plan.label}</span>
                  {plan.note && <span className="text-[#8B7355] text-xs mt-1 block" data-id="element-1046">{plan.note}</span>}
                  {plan.value !== 'none' && !plan.custom && subRevenueUSD && subRevenueUSD > 0 && <span className="text-[#5F6F52] text-xs mt-1 block font-medium" data-id="element-1047">
                      At your volume: USD ${calcWinePikCostUSD(plan, subRevenueUSD).toFixed(0)}/mo (~AUD ${Math.round(calcWinePikCostUSD(plan, subRevenueUSD) * rate)}/mo)
                    </span>}
                </div>
                <div className="flex items-center gap-3 flex-shrink-0" data-id="element-1048">
                  {plan.custom ? <span className="text-[#8B7355] text-sm font-medium" data-id="element-1049">Custom</span> : plan.priceUSD > 0 ? <span className="text-[#8B7355] text-sm font-medium text-right" data-id="element-1050">USD ${plan.priceUSD}/mo<br data-id="element-1051" /><span className="text-xs" data-id="element-1052">(~AUD ${Math.round(plan.priceUSD * rate)}/mo)</span></span> : null}
                  <RadioDot selected={selected === plan.value} data-id="element-1053" />
                </div>
              </div>
            </button>;
      })}
      </div>
      {label === 'WinePik' && subRevenueUSD !== undefined && subRevenueUSD > 0 && <div className="mt-4 p-4 bg-[#FAF7F2] rounded-lg border border-[#D4A57420]" data-id="element-1054">
          <p className="text-[#2C2416] text-xs font-semibold mb-2" data-id="element-1055">Plan optimiser — based on your subscription billing</p>
          <div className="space-y-1 text-xs text-[#8B7355]" data-id="element-1056">
            <p data-id="element-1057">• <strong className="text-[#2C2416]" data-id="element-1058">Starter</strong> — best up to USD $2,500/mo (flat $99, no overage)</p>
            <p data-id="element-1059">• <strong className="text-[#2C2416]" data-id="element-1060">Growth</strong> — best USD $2,500–$22,500/mo ($299 + 1.75% above $5k)</p>
            <p data-id="element-1061">• <strong className="text-[#2C2416]" data-id="element-1062">Pro</strong> — best above USD $22,500/mo ($449 + 1.25% above $10k)</p>
          </div>
          <p className="text-[#5F6F52] text-xs font-medium mt-2" data-id="element-1063">
            At your volume (AUD ${Math.round(subRevenueUSD * rate).toLocaleString()}/mo ≈ USD ${subRevenueUSD.toFixed(0)}/mo):{' '}
            {recommendedPlan === 'starter' ? 'Starter' : recommendedPlan === 'growth' ? 'Growth' : 'Pro'} is the most cost-effective plan.
          </p>
        </div>}
    </div>;
}
export function WineryCalculator() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);
  const [usdToAud, setUsdToAud] = useState<number>(FALLBACK_RATE);
  const [rateLoading, setRateLoading] = useState(true);
  const [rateError, setRateError] = useState(false);
  const [rateTimestamp, setRateTimestamp] = useState<string>('');
  useEffect(() => {
    const fetchRate = async () => {
      setRateLoading(true);
      setRateError(false);
      try {
        const res = await fetch('https://open.er-api.com/v6/latest/USD');
        const data = await res.json();
        if (data?.rates?.AUD) {
          setUsdToAud(data.rates.AUD);
          const updated = new Date(data.time_last_update_utc);
          setRateTimestamp(updated.toLocaleDateString('en-AU', {
            day: 'numeric',
            month: 'short',
            year: 'numeric'
          }));
        } else {
          setRateError(true);
        }
      } catch {
        setRateError(true);
      } finally {
        setRateLoading(false);
      }
    };
    fetchRate();
  }, []);
  const [txCount, setTxCount] = useState<number | ''>('');
  const [monthlyRevenue, setMonthlyRevenue] = useState<number | ''>('');
  const [posTxCount, setPosTxCount] = useState<number | ''>('');
  const [posRevenue, setPosRevenue] = useState<number | ''>('');
  const [c7Plan, setC7Plan] = useState<Commerce7Plan>('none');
  const [shopifyPlan, setShopifyPlan] = useState<ShopifyPlan>('none');
  const [subBillingVolume, setSubBillingVolume] = useState<number | ''>('');
  const [subBillingIsTotal, setSubBillingIsTotal] = useState(false);
  const [totalSubBilling, setTotalSubBilling] = useState<number | ''>('');
  const [posPro, setPosPro] = useState(false);
  const [posLocations, setPosLocations] = useState(1);
  const [posTerminal, setPosTerminal] = useState<POSTerminal>('shopify');
  const [klaviyoContacts, setKlaviyoContacts] = useState<number | ''>('');
  const [winePikPlan, setWinePikPlan] = useState<WinePikPlan>('none');
  const [wineHubPlan, setWineHubPlan] = useState<WineHubPlan>('none');
  const [awtomicPlan, setAwtomicPlan] = useState<AwtomicPlan>('none');
  const [customApps, setCustomApps] = useState<CustomApp[]>([]);
  const [customAppName, setCustomAppName] = useState('');
  const [customAppCost, setCustomAppCost] = useState('');
  const hasShopify = shopifyPlan !== 'none';
  const hasC7 = c7Plan !== 'none' && c7Plan !== 'enterprise';
  const c7Config = C7_PLANS.find(p => p.value === c7Plan);
  const shopifyConfig = SHOPIFY_PLANS.find(p => p.value === shopifyPlan);
  const winePikConfig = WINEPIK_PLANS.find(p => p.value === winePikPlan);
  const wineHubConfig = WINEHUB_PLANS.find(p => p.value === wineHubPlan);
  const awtomicConfig = AWTOMIC_PLANS.find(p => p.value === awtomicPlan);
  const addCustomApp = () => {
    if (customAppName.trim() && customAppCost !== '') {
      setCustomApps(prev => [...prev, {
        id: Date.now().toString(),
        name: customAppName.trim(),
        costUSD: parseFloat(customAppCost) || 0
      }]);
      setCustomAppName('');
      setCustomAppCost('');
    }
  };
  const removeCustomApp = (id: string) => setCustomApps(prev => prev.filter(app => app.id !== id));
  const txCountNum = Number(txCount) || 0;
  const txMonthlyRevenue = Number(monthlyRevenue) || 0;
  const hasTxInputs = txCountNum > 0 && txMonthlyRevenue > 0;
  const avgOrderValueCalc = txCountNum > 0 ? txMonthlyRevenue / txCountNum : 0;
  const c7TxSectionFee = hasTxInputs && hasC7 && c7Config ? txMonthlyRevenue * c7Config.txFee / 100 : 0;
  const c7StripeSectionFee = hasTxInputs && hasC7 ? txMonthlyRevenue * STRIPE_RATE / 100 + txCountNum * STRIPE_FIXED : 0;
  const shopifyTxSectionFee = hasTxInputs && hasShopify && shopifyConfig ? txMonthlyRevenue * shopifyConfig.ccRate / 100 + txCountNum * shopifyConfig.ccFixed : 0;
  const legacyVol = Number(subBillingVolume) || 0;
  const totalSubBillingAUD = subBillingIsTotal ? legacyVol : Number(totalSubBilling) || 0;
  const totalSubBillingUSD = usdToAud > 0 ? totalSubBillingAUD / usdToAud : 0;
  const hasSubBilling = totalSubBillingAUD > 0;
  const recommendedWinePikPlan = totalSubBillingUSD > 0 ? getRecommendedWinePikPlan(totalSubBillingUSD) : undefined;
  const posTxCountNum = Number(posTxCount) || 0;
  const posRevenueNum = Number(posRevenue) || 0;
  const hasPosInputs = posTxCountNum > 0 && posRevenueNum > 0;
  const posAvgOrder = posTxCountNum > 0 ? posRevenueNum / posTxCountNum : 0;
  let posTxFeeAUD = 0;
  let posTxFeeLabel = '';
  if (hasPosInputs && posPro && hasShopify && shopifyConfig && shopifyConfig.value !== 'plus') {
    if (posTerminal === 'shopify') {
      posTxFeeAUD = posRevenueNum * shopifyConfig.inPersonRate / 100;
      posTxFeeLabel = `${shopifyConfig.inPersonRate}% in-person rate`;
    } else {
      posTxFeeAUD = posRevenueNum * shopifyConfig.thirdPartyFee / 100;
      posTxFeeLabel = `${shopifyConfig.thirdPartyFee}% third-party gateway fee`;
    }
  }
  const kCostUSD = getKlaviyoCostUSD(Number(klaviyoContacts) || 0);
  const klaviyoNeedsContact = Number(klaviyoContacts) > 0 && kCostUSD === -1;
  const klaviyoCostAUD = Number(klaviyoContacts) > 0 && kCostUSD > 0 ? Math.round(kCostUSD * usdToAud) : 0;
  const customAppsTotalAUD = Math.round(customApps.reduce((sum, app) => sum + app.costUSD, 0) * usdToAud);
  const c7PlatformCostAUD = hasC7 && c7Config ? c7Config.priceAUD : 0;
  const c7TxCostAUD = hasTxInputs && hasC7 && c7Config ? Math.round(c7TxSectionFee + c7StripeSectionFee) : 0;
  const c7TotalAUD = c7PlatformCostAUD + c7TxCostAUD + klaviyoCostAUD + customAppsTotalAUD;
  const shopifyPlatformCostAUD = hasShopify && shopifyConfig ? shopifyConfig.priceAUD : 0;
  const posCostAUD = posPro ? 129 * posLocations : 0;
  const shopifyTxCostAUD = hasTxInputs && hasShopify && shopifyConfig ? Math.round(shopifyTxSectionFee) : 0;
  const sharedCostAUD = shopifyPlatformCostAUD + shopifyTxCostAUD + posCostAUD + Math.round(posTxFeeAUD) + klaviyoCostAUD + customAppsTotalAUD;
  const winePikFlatAUD = winePikConfig && !winePikConfig.custom ? Math.round(winePikConfig.priceUSD * usdToAud) : 0;
  const winePikRevFeeAUD = winePikConfig && !winePikConfig.custom && hasSubBilling ? Math.round(calcRevenueFeeUSD(winePikConfig, totalSubBillingUSD) * usdToAud) : 0;
  const wineHubFlatAUD = wineHubConfig && !wineHubConfig.custom ? Math.round(wineHubConfig.priceUSD * usdToAud) : 0;
  const wineHubRevFeeAUD = wineHubConfig && !wineHubConfig.custom && hasSubBilling ? Math.round(calcRevenueFeeUSD(wineHubConfig, totalSubBillingUSD) * usdToAud) : 0;
  const awtomicFlatAUD = awtomicConfig && !awtomicConfig.custom ? Math.round(awtomicConfig.priceUSD * usdToAud) : 0;
  const awtomicRevFeeAUD = awtomicConfig && !awtomicConfig.custom && hasSubBilling ? Math.round(calcRevenueFeeUSD(awtomicConfig, totalSubBillingUSD) * usdToAud) : 0;
  const shopifyWinePikTotal = winePikPlan !== 'none' ? sharedCostAUD + winePikFlatAUD + winePikRevFeeAUD : 0;
  const shopifyWineHubTotal = wineHubPlan !== 'none' ? sharedCostAUD + wineHubFlatAUD + wineHubRevFeeAUD : 0;
  const shopifyAwtomicTotal = awtomicPlan !== 'none' ? sharedCostAUD + awtomicFlatAUD + awtomicRevFeeAUD : 0;
  const shopifyNoAppTotal = sharedCostAUD;
  interface ComparisonColumn {
    id: string;
    header: string;
    subheader: string;
    total: number;
    isC7: boolean;
    appConfig?: AppPlanConfig;
    appFlatAUD?: number;
    appRevFeeAUD?: number;
  }
  const columns: ComparisonColumn[] = [];
  if (hasC7 || c7Plan === 'enterprise') {
    columns.push({
      id: 'c7',
      header: 'Commerce7',
      subheader: c7Config?.label || 'Enterprise',
      total: c7TotalAUD,
      isC7: true
    });
  }
  if (hasShopify) {
    const hasAnyWineApp = winePikPlan !== 'none' || wineHubPlan !== 'none' || awtomicPlan !== 'none';
    if (!hasAnyWineApp) columns.push({
      id: 'shopify_base',
      header: `Shopify ${shopifyConfig?.label}`,
      subheader: 'No wine club app',
      total: shopifyNoAppTotal,
      isC7: false
    });
    if (winePikPlan !== 'none' && winePikConfig) columns.push({
      id: 'shopify_winepik',
      header: `Shopify ${shopifyConfig?.label}`,
      subheader: winePikConfig.label,
      total: shopifyWinePikTotal,
      isC7: false,
      appConfig: winePikConfig,
      appFlatAUD: winePikFlatAUD,
      appRevFeeAUD: winePikRevFeeAUD
    });
    if (wineHubPlan !== 'none' && wineHubConfig) columns.push({
      id: 'shopify_winehub',
      header: `Shopify ${shopifyConfig?.label}`,
      subheader: wineHubConfig.label,
      total: shopifyWineHubTotal,
      isC7: false,
      appConfig: wineHubConfig,
      appFlatAUD: wineHubFlatAUD,
      appRevFeeAUD: wineHubRevFeeAUD
    });
    if (awtomicPlan !== 'none' && awtomicConfig) columns.push({
      id: 'shopify_awtomic',
      header: `Shopify ${shopifyConfig?.label}`,
      subheader: awtomicConfig.label,
      total: shopifyAwtomicTotal,
      isC7: false,
      appConfig: awtomicConfig,
      appFlatAUD: awtomicFlatAUD,
      appRevFeeAUD: awtomicRevFeeAUD
    });
  }
  const minTotal = columns.length > 0 ? Math.min(...columns.filter(c => !c.isC7 || c7Plan !== 'enterprise').map(c => c.total)) : 0;
  const colCount = columns.length;
  const gridStyle = {
    gridTemplateColumns: `200px repeat(${colCount}, 1fr)`
  };
  const fmt = (n: number) => `AUD $${n.toLocaleString()}/mo`;
  const fmtUSD = (usd: number) => usd > 0 ? `USD $${usd.toFixed(0)}/mo\n(~AUD $${Math.round(usd * usdToAud)}/mo)` : '—';
  return <div className="min-h-screen bg-[#FAF7F2] font-['Inter']" data-id="element-1064">
      <Header data-id="element-1065" />

      <section className="pt-32 pb-8 px-6 md:px-10" data-id="element-1066">
        <div className="max-w-[1400px] mx-auto" data-id="element-1067">
          <motion.h1 initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.6
        }} className="text-[#2C2416] text-[clamp(36px,6vw,64px)] font-light leading-[1.1] tracking-[-3px] mb-4" data-id="element-1068">Winery Platform Cost Estimator</motion.h1>
          <motion.p initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.6,
          delay: 0.1
        }} className="text-[#8B7355] text-xl max-w-2xl leading-relaxed" data-id="element-1069">Select your platforms and apps to compare total monthly costs side by side.</motion.p>
          <motion.div initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} transition={{
          delay: 0.3
        }} className="mt-4 inline-flex items-center gap-2 px-3 py-1.5 bg-white rounded-full border border-[#D4A57420] text-xs text-[#8B7355]" data-id="element-1070">
            <RefreshCwIcon className={`w-3 h-3 ${rateLoading ? 'animate-spin' : ''}`} data-id="element-1071" />
            {rateLoading ? 'Fetching live USD/AUD rate…' : rateError ? `USD/AUD: ${FALLBACK_RATE.toFixed(4)} (fallback)` : `Live USD/AUD: ${usdToAud.toFixed(4)} — updated ${rateTimestamp}`}
          </motion.div>
        </div>
      </section>

      {/* SECTION 1: Online transaction volume */}
      <motion.section {...fadeIn} className="py-12 px-6 md:px-10 bg-white" data-id="element-1072">
        <div className="max-w-[1400px] mx-auto" data-id="element-1073">
          <h2 className="text-[#2C2416] text-[clamp(28px,4vw,40px)] font-light tracking-[-2px] mb-2" data-id="element-1074">Your online transaction volume</h2>
          <p className="text-[#8B7355] text-lg mb-8" data-id="element-1075">Online / eCommerce orders only. Enter POS volume separately below.</p>
          <div className="bg-[#FAF7F2] rounded-xl border border-[#D4A57420] p-6 max-w-2xl" data-id="element-1076">
            <div className="grid sm:grid-cols-2 gap-6 mb-6" data-id="element-1077">
              <div className="flex flex-col" data-id="element-1078">
                <label className="block text-[#2C2416] font-medium mb-1 text-sm" data-id="element-1079">Online transactions per month</label>
                <p className="text-[#8B7355] text-xs mb-2" data-id="element-1080">Number of online orders per month.</p>
                <div className="flex items-center gap-2 mt-auto" data-id="element-1081">
                  <input type="number" min={0} value={txCount} onChange={e => setTxCount(e.target.value === '' ? '' : parseInt(e.target.value) || 0)} placeholder="e.g. 500" className={inputClass} data-id="element-1082" />
                </div>
              </div>
              <div className="flex flex-col" data-id="element-1083">
                <label className="block text-[#2C2416] font-medium mb-1 text-sm" data-id="element-1084">Monthly online revenue (AUD)</label>
                <p className="text-[#8B7355] text-xs mb-2" data-id="element-1085">Average monthly DTC revenue.</p>
                <div className="flex items-center gap-2 mt-auto" data-id="element-1086">
                  <span className="text-[#8B7355] font-medium text-sm" data-id="element-1087">$</span>
                  <input type="number" min={0} value={monthlyRevenue} onChange={e => setMonthlyRevenue(e.target.value === '' ? '' : parseFloat(e.target.value) || 0)} placeholder="e.g. 60000" className={inputClass} data-id="element-1088" />
                </div>
              </div>
            </div>
            {hasTxInputs && <div className="p-4 bg-white rounded-lg border border-[#D4A57420]" data-id="element-1089">
                <p className="text-[#2C2416] font-medium text-sm" data-id="element-1090">Monthly online revenue: <span className="text-[#5F6F52]" data-id="element-1091">AUD ${txMonthlyRevenue.toLocaleString()}</span></p>
                <p className="text-[#8B7355] text-xs mt-1" data-id="element-1092">{txCountNum.toLocaleString()} transactions — avg AUD ${avgOrderValueCalc.toFixed(2)}</p>
              </div>}
          </div>
        </div>
      </motion.section>

      {/* SECTION 2: Platform */}
      <motion.section {...fadeIn} className="py-12 px-6 md:px-10 bg-[#FAF7F2]" data-id="element-1093">
        <div className="max-w-[1400px] mx-auto" data-id="element-1094">
          <h2 className="text-[#2C2416] text-[clamp(28px,4vw,40px)] font-light tracking-[-2px] mb-10" data-id="element-1095">Platform</h2>
          <div className="grid md:grid-cols-2 gap-10" data-id="element-1096">
            <div data-id="element-1097">
              <h3 className="text-[#2C2416] text-lg font-medium mb-4" data-id="element-1098">Winery platform</h3>
              <div className="space-y-3" data-id="element-1099">
                {C7_PLANS.map(opt => <button key={opt.value} onClick={() => setC7Plan(opt.value)} className={`${cardBase} ${c7Plan === opt.value ? cardSelected : cardUnselected}`} data-id="element-1100">
                    <div className="flex items-start justify-between gap-4" data-id="element-1101">
                      <div className="flex-1 min-w-0" data-id="element-1102">
                        <span className="text-[#2C2416] font-medium block" data-id="element-1103">{opt.label}</span>
                        {opt.revenueRange && <span className="text-[#8B7355] text-xs mt-1 block" data-id="element-1104">{opt.revenueRange}</span>}
                        {!opt.custom && opt.value !== 'none' && <span className="text-[#8B7355] text-xs mt-1 block" data-id="element-1105">C7 fee: {opt.txFee}% + Stripe: 1.7% + 30¢</span>}
                      </div>
                      <div className="flex items-center gap-3 flex-shrink-0" data-id="element-1106">
                        {opt.custom ? <span className="text-[#8B7355] text-sm font-medium" data-id="element-1107">Custom</span> : opt.value !== 'none' ? <span className="text-[#8B7355] text-sm font-medium" data-id="element-1108">AUD ${opt.priceAUD.toLocaleString()}/mo</span> : null}
                        <RadioDot selected={c7Plan === opt.value} data-id="element-1109" />
                      </div>
                    </div>
                  </button>)}
              </div>
            </div>
            <div data-id="element-1110">
              <h3 className="text-[#2C2416] text-lg font-medium mb-4" data-id="element-1111">Shopify plan</h3>
              <div className="space-y-3" data-id="element-1112">
                {SHOPIFY_PLANS.map(opt => <button key={opt.value} onClick={() => setShopifyPlan(opt.value)} className={`${cardBase} ${shopifyPlan === opt.value ? cardSelected : cardUnselected}`} data-id="element-1113">
                    <div className="flex items-start justify-between gap-4" data-id="element-1114">
                      <div className="flex-1 min-w-0" data-id="element-1115">
                        <span className="text-[#2C2416] font-medium block" data-id="element-1116">{opt.label}</span>
                        {opt.value === 'plus' && <span className="text-[#8B7355] text-xs mt-1 block" data-id="element-1117">Custom rates</span>}
                        {opt.value !== 'none' && opt.value !== 'plus' && <><span className="text-[#8B7355] text-xs mt-1 block" data-id="element-1118">Shopify Payments: {opt.ccRate}% + 30¢</span><span className="text-[#8B7355] text-xs mt-0.5 block" data-id="element-1119">3rd-party: {opt.thirdPartyFee}%</span></>}
                      </div>
                      <div className="flex items-center gap-3 flex-shrink-0" data-id="element-1120">
                        {opt.value !== 'none' && <span className="text-[#8B7355] text-sm font-medium" data-id="element-1121">{opt.value === 'plus' ? 'From AUD $3,700/mo' : `AUD $${opt.priceAUD}/mo`}</span>}
                        <RadioDot selected={shopifyPlan === opt.value} data-id="element-1122" />
                      </div>
                    </div>
                  </button>)}
              </div>
              {hasShopify && shopifyConfig && <div className="mt-6 p-5 bg-white rounded-xl border-2 border-[#D4A57420]" data-id="element-1123">
                  <div className="flex items-center gap-2 mb-2" data-id="element-1124">
                    <TrendingDownIcon className="w-5 h-5 text-[#5F6F52]" data-id="element-1125" />
                    <h4 className="text-[#2C2416] font-medium text-sm" data-id="element-1126">Subscription billing volume</h4>
                  </div>
                  <p className="text-[#8B7355] text-xs leading-relaxed mb-4" data-id="element-1127">Used for legacy gateway decay AND app revenue fee calculations (WineHub, WinePik, Awtomic all charge a % on subscription revenue).</p>
                  <label className="block text-[#2C2416] font-medium text-sm mb-2" data-id="element-1128">Monthly sub billing on legacy gateway (AUD)</label>
                  <div className="flex items-center gap-2 mb-3" data-id="element-1129">
                    <span className="text-[#8B7355] font-medium text-sm" data-id="element-1130">AUD $</span>
                    <input type="number" min={0} value={subBillingVolume} onChange={e => setSubBillingVolume(e.target.value === '' ? '' : parseFloat(e.target.value) || 0)} placeholder="e.g. 20000" className={inputClass + ' max-w-[200px]'} data-id="element-1131" />
                    {legacyVol > 0 && <p className="text-[#8B7355] text-xs mt-1" data-id="element-1132">≈ USD ${Math.round(legacyVol / usdToAud).toLocaleString()}/mo at live rate</p>}
                  </div>
                  <label className="flex items-start gap-2 cursor-pointer mb-4" data-id="element-1133">
                    <input type="checkbox" checked={subBillingIsTotal} onChange={e => setSubBillingIsTotal(e.target.checked)} className="mt-0.5 w-4 h-4 rounded border-[#D4A57440] text-[#5F6F52] focus:ring-[#5F6F52] flex-shrink-0" data-id="element-1134" />
                    <span className="text-[#2C2416] text-sm" data-id="element-1135">This is also my <strong data-id="element-1136">total</strong> monthly subscription billing (use for app revenue fee calculations)</span>
                  </label>
                  {!subBillingIsTotal && <div className="mb-4 p-4 bg-[#FAF7F2] rounded-lg border border-[#D4A57420]" data-id="element-1137">
                      <label className="block text-[#2C2416] font-medium text-sm mb-1" data-id="element-1138">Total monthly subscription billing (AUD)</label>
                      <p className="text-[#8B7355] text-xs mb-2" data-id="element-1139">Full subscription billing — used to calculate app revenue fees.</p>
                      <div className="flex items-center gap-2" data-id="element-1140">
                        <span className="text-[#8B7355] font-medium text-sm" data-id="element-1141">AUD $</span>
                        <input type="number" min={0} value={totalSubBilling} onChange={e => setTotalSubBilling(e.target.value === '' ? '' : parseFloat(e.target.value) || 0)} placeholder="e.g. 30000" className={inputClass + ' max-w-[200px]'} data-id="element-1142" />
                        {totalSubBillingAUD > 0 && <p className="text-[#8B7355] text-xs mt-1" data-id="element-1143">≈ USD ${Math.round(totalSubBillingUSD).toLocaleString()}/mo at live rate</p>}
                      </div>
                    </div>}
                  {legacyVol > 0 && <div data-id="element-1144">
                      <div className="flex items-center gap-2 mb-2" data-id="element-1145"><TrendingDownIcon className="w-4 h-4 text-[#5F6F52]" data-id="element-1146" /><p className="text-[#2C2416] text-sm font-medium" data-id="element-1147">Legacy gateway fee — projected decay</p></div>
                      <p className="text-[#8B7355] text-xs mb-3" data-id="element-1148">{shopifyConfig.value === 'plus' ? 'Shopify Plus has custom gateway rates — 0.2% used as reference.' : `Based on ${shopifyConfig.thirdPartyFee}% 3rd-party fee (${shopifyConfig.label}), decaying ~30%/year.`}</p>
                      <div className="grid grid-cols-4 gap-2" data-id="element-1149">
                        {LEGACY_DECAY.map((factor, i) => {
                    const vol = legacyVol * factor;
                    const cost = vol * shopifyConfig.thirdPartyFee / 100;
                    const isNow = i === 0;
                    return <div key={i} className={`p-3 rounded-lg text-center ${isNow ? 'bg-[#5F6F52]/10 border border-[#5F6F52]/20' : 'bg-[#FAF7F2]'}`} data-id="element-1150">
                              <p className="text-[#8B7355] text-xs mb-1" data-id="element-1151">{LEGACY_DECAY_LABELS[i]}</p>
                              <p className={`text-sm font-medium ${isNow ? 'text-[#5F6F52]' : 'text-[#2C2416]'}`} data-id="element-1152">AUD ${cost.toFixed(0)}/mo</p>
                              <p className="text-[#8B7355] text-xs mt-1" data-id="element-1153">${Math.round(vol).toLocaleString()} vol</p>
                            </div>;
                  })}
                      </div>
                      <p className="text-[#8B7355] text-xs mt-3 italic" data-id="element-1154">Illustrative estimate only.</p>
                    </div>}
                </div>}
            </div>
          </div>
        </div>
      </motion.section>

      {/* SECTION 3: POS */}
      <motion.section {...fadeIn} className="py-12 px-6 md:px-10 bg-white" data-id="element-1155">
        <div className="max-w-[1400px] mx-auto" data-id="element-1156">
          <div className="flex items-center gap-3 mb-8" data-id="element-1157">
            <h2 className="text-[#2C2416] text-[clamp(28px,4vw,40px)] font-light tracking-[-2px]" data-id="element-1158">Point of sale</h2>
            <span className="px-2.5 py-0.5 bg-[#5F6F52]/10 text-[#5F6F52] text-xs font-medium rounded-full" data-id="element-1159">Shopify only</span>
          </div>
          <Toggle value={posPro} onChange={setPosPro} label="Do you need Shopify POS Pro?" data-id="element-1160" />
          {posPro && <div className="mt-6 space-y-6 max-w-2xl" data-id="element-1161">
              <div className="bg-[#FAF7F2] p-6 rounded-xl border border-[#D4A57420]" data-id="element-1162">
                <label className="block text-[#2C2416] font-medium mb-2 text-sm" data-id="element-1163">How many locations?</label>
                <input type="number" min={1} value={posLocations} onChange={e => setPosLocations(Math.max(1, parseInt(e.target.value) || 1))} className={inputClass + ' max-w-[120px]'} data-id="element-1164" />
                <p className="text-[#8B7355] text-sm mt-3" data-id="element-1165">AUD $129/location/mo = <span className="text-[#2C2416] font-medium" data-id="element-1166">AUD ${(129 * posLocations).toLocaleString()}/mo</span></p>
              </div>
              <div className="bg-[#FAF7F2] p-6 rounded-xl border border-[#D4A57420]" data-id="element-1167">
                <h3 className="text-[#2C2416] font-medium text-sm mb-1" data-id="element-1168">In-person sales volume</h3>
                <p className="text-[#8B7355] text-xs mb-4" data-id="element-1169">Separate from online revenue — cellar door / in-person sales.</p>
                <div className="grid sm:grid-cols-2 gap-6" data-id="element-1170">
                  <div data-id="element-1171">
                    <label className="block text-[#2C2416] font-medium mb-2 text-sm" data-id="element-1172">In-person transactions/month</label>
                    <input type="number" min={0} value={posTxCount} onChange={e => setPosTxCount(e.target.value === '' ? '' : parseInt(e.target.value) || 0)} placeholder="e.g. 200" className={inputClass} data-id="element-1173" />
                  </div>
                  <div data-id="element-1174">
                    <label className="block text-[#2C2416] font-medium mb-2 text-sm" data-id="element-1175">Monthly in-person revenue (AUD)</label>
                    <div className="flex items-center gap-2" data-id="element-1176"><span className="text-[#8B7355] font-medium text-sm" data-id="element-1177">$</span><input type="number" min={0} value={posRevenue} onChange={e => setPosRevenue(e.target.value === '' ? '' : parseFloat(e.target.value) || 0)} placeholder="e.g. 15000" className={inputClass} data-id="element-1178" /></div>
                  </div>
                </div>
                {hasPosInputs && <div className="mt-4 p-4 bg-white rounded-lg border border-[#D4A57420]" data-id="element-1179">
                    <p className="text-[#2C2416] font-medium text-sm" data-id="element-1180">In-person revenue: <span className="text-[#5F6F52]" data-id="element-1181">AUD ${posRevenueNum.toLocaleString()}</span></p>
                    <p className="text-[#8B7355] text-xs mt-1" data-id="element-1182">{posTxCountNum.toLocaleString()} transactions — avg AUD ${posAvgOrder.toFixed(2)}</p>
                    {!hasShopify && <p className="text-[#8B7355] text-xs mt-2 italic" data-id="element-1183">Select a Shopify plan above to see payment fee estimates.</p>}
                    {hasShopify && shopifyConfig && shopifyConfig.value === 'plus' && <p className="text-[#8B7355] text-xs mt-2 italic" data-id="element-1184">Shopify Plus has custom in-person rates.</p>}
                    {posTxFeeAUD > 0 && <p className="text-[#2C2416] font-medium text-sm mt-2" data-id="element-1185">Est. payment fee: <span className="text-[#5F6F52]" data-id="element-1186">AUD ${Math.round(posTxFeeAUD).toLocaleString()}/mo</span> <span className="text-[#8B7355] text-xs font-normal" data-id="element-1187">({posTxFeeLabel})</span></p>}
                  </div>}
              </div>
              <div data-id="element-1188">
                <h3 className="text-[#2C2416] font-medium text-sm mb-3" data-id="element-1189">Payment terminal</h3>
                <div className="grid grid-cols-2 gap-3" data-id="element-1190">
                  <button onClick={() => setPosTerminal('shopify')} className={`${cardBase} ${posTerminal === 'shopify' ? cardSelected : cardUnselected}`} data-id="element-1191">
                    <div className="flex items-start justify-between gap-3" data-id="element-1192"><div data-id="element-1193"><span className="text-[#2C2416] font-medium block text-sm" data-id="element-1194">Shopify card reader</span>{hasShopify && shopifyConfig && shopifyConfig.value !== 'plus' && <span className="text-[#8B7355] text-xs mt-1 block" data-id="element-1195">In-person rate: {shopifyConfig.inPersonRate}%</span>}<span className="text-[#8B7355] text-xs mt-1 block" data-id="element-1196">No additional gateway fee</span></div><RadioDot selected={posTerminal === 'shopify'} data-id="element-1197" /></div>
                  </button>
                  <button onClick={() => setPosTerminal('own')} className={`${cardBase} ${posTerminal === 'own' ? cardSelected : cardUnselected}`} data-id="element-1198">
                    <div className="flex items-start justify-between gap-3" data-id="element-1199"><div data-id="element-1200"><span className="text-[#2C2416] font-medium block text-sm" data-id="element-1201">Own terminal</span>{hasShopify && shopifyConfig && shopifyConfig.value !== 'plus' && <span className="text-[#8B7355] text-xs mt-1 block" data-id="element-1202">+{shopifyConfig.thirdPartyFee}% gateway fee applies</span>}<span className="text-[#8B7355] text-xs mt-1 block" data-id="element-1203">e.g. existing EFTPOS terminal</span></div><RadioDot selected={posTerminal === 'own'} data-id="element-1204" /></div>
                  </button>
                </div>
                {posTerminal === 'own' && hasShopify && shopifyConfig && <div className="mt-3 p-4 bg-[#FAF7F2] rounded-lg border border-[#D4A57420] flex gap-3" data-id="element-1205"><InfoIcon className="w-5 h-5 text-[#8B7355] flex-shrink-0 mt-0.5" data-id="element-1206" /><p className="text-[#8B7355] text-sm leading-relaxed" data-id="element-1207">{shopifyConfig.value === 'plus' ? 'Shopify Plus has custom gateway rates. Contact Shopify.' : `Using your own terminal means Shopify applies a ${shopifyConfig.thirdPartyFee}% third-party gateway fee.`}</p></div>}
              </div>
            </div>}
        </div>
      </motion.section>

      {/* SECTION 4: Apps */}
      <motion.section {...fadeIn} className="py-12 px-6 md:px-10 bg-[#FAF7F2]" data-id="element-1208">
        <div className="max-w-[1400px] mx-auto" data-id="element-1209">
          <div className="flex items-center gap-3 mb-2" data-id="element-1210">
            <h2 className="text-[#2C2416] text-[clamp(28px,4vw,40px)] font-light tracking-[-2px]" data-id="element-1211">Apps</h2>
            <span className="px-2.5 py-0.5 bg-[#5F6F52]/10 text-[#5F6F52] text-xs font-medium rounded-full" data-id="element-1212">Shopify only</span>
          </div>
          <p className="text-[#8B7355] text-lg mb-4" data-id="element-1213">Select which apps to compare. Each selected wine club app gets its own column in the comparison. Commerce7 includes these features — these apps are for Shopify builds only.</p>
          {!hasSubBilling && (winePikPlan !== 'none' || wineHubPlan !== 'none' || awtomicPlan !== 'none') && <div className="mb-6 p-4 bg-amber-50 border border-amber-200 rounded-lg flex gap-3" data-id="element-1214">
              <AlertTriangleIcon className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" data-id="element-1215" />
              <p className="text-amber-800 text-sm" data-id="element-1216">Enter your total monthly subscription billing in the Platform section above to calculate accurate revenue fees.</p>
            </div>}
          <div className="space-y-10" data-id="element-1217">
            <div className="bg-white rounded-xl border border-[#D4A57420] p-6" data-id="element-1218">
              <p className="text-[#8B7355] text-xs font-semibold tracking-[1.5px] uppercase mb-2" data-id="element-1219">Email marketing</p>
              <p className="text-[#2C2416] font-medium mb-4" data-id="element-1220">Klaviyo <span className="text-[#8B7355] text-xs font-normal" data-id="element-1221">(applies to both platforms)</span></p>
              <label className="block text-[#2C2416] text-sm font-medium mb-2" data-id="element-1222">How many email contacts?</label>
              <input type="number" min={0} value={klaviyoContacts} onChange={e => setKlaviyoContacts(e.target.value === '' ? '' : parseInt(e.target.value) || 0)} placeholder="e.g. 5000" className={inputClass + ' max-w-[200px] mb-3'} data-id="element-1223" />
              {Number(klaviyoContacts) > 0 && <p className="text-[#2C2416] font-medium text-sm mb-3" data-id="element-1224">Estimated: {getKlaviyoTierLabel(Number(klaviyoContacts), usdToAud)}</p>}
              <div className="space-y-1 text-[#8B7355] text-xs mb-2" data-id="element-1225">
                <p data-id="element-1226">Up to 500 — Free &nbsp;|&nbsp; 501–1,000 — USD $20/mo &nbsp;|&nbsp; 1,001–5,000 — USD $45/mo</p>
                <p data-id="element-1227">5,001–10,000 — USD $100/mo &nbsp;|&nbsp; 10,001–25,000 — USD $175/mo &nbsp;|&nbsp; 25,001+ — Contact Klaviyo</p>
              </div>
              <p className="text-[#8B7355] text-xs italic" data-id="element-1228">Bills in USD. AUD estimates at live rate of {rateLoading ? '…' : usdToAud.toFixed(4)}.</p>
            </div>
            {hasShopify && <div className="grid md:grid-cols-3 gap-8" data-id="element-1229">
                <AppPlanSelector label="WinePik" category="Wine club subscriptions" plans={WINEPIK_PLANS} selected={winePikPlan} onSelect={v => setWinePikPlan(v as WinePikPlan)} rate={usdToAud} recommendedPlan={recommendedWinePikPlan} subRevenueUSD={totalSubBillingUSD || undefined} data-id="element-1230" />
                <AppPlanSelector label="WineHub" category="Wine club & compliance" plans={WINEHUB_PLANS} selected={wineHubPlan} onSelect={v => setWineHubPlan(v as WineHubPlan)} rate={usdToAud} data-id="element-1231" />
                <AppPlanSelector label="Awtomic" category="Subscriptions" plans={AWTOMIC_PLANS} selected={awtomicPlan} onSelect={v => setAwtomicPlan(v as AwtomicPlan)} rate={usdToAud} data-id="element-1232" />
              </div>}
          </div>
          <div className="border-t border-[#D4A57420] pt-8 mt-10" data-id="element-1233">
            <h3 className="text-[#2C2416] text-lg font-medium mb-1" data-id="element-1234">Add your own app <span className="text-[#8B7355] text-sm font-normal" data-id="element-1235">(applies to both platforms)</span></h3>
            <p className="text-[#8B7355] text-xs mb-4" data-id="element-1236">Enter monthly cost in USD — converted at the live rate.</p>
            {customApps.length > 0 && <div className="space-y-2 mb-4" data-id="element-1237">
                {customApps.map(app => <div key={app.id} className="flex items-center justify-between bg-white p-3 rounded-lg border border-[#D4A57420]" data-id="element-1238">
                    <div data-id="element-1239"><span className="text-[#2C2416] font-medium text-sm" data-id="element-1240">{app.name}</span><span className="text-[#8B7355] text-sm ml-2" data-id="element-1241">USD ${app.costUSD}/mo (~AUD ${Math.round(app.costUSD * usdToAud)}/mo)</span></div>
                    <button onClick={() => removeCustomApp(app.id)} className="text-[#8B7355] hover:text-red-500 transition-colors" data-id="element-1242"><XIcon className="w-4 h-4" data-id="element-1243" /></button>
                  </div>)}
              </div>}
            <div className="flex flex-col sm:flex-row gap-3" data-id="element-1244">
              <input type="text" value={customAppName} onChange={e => setCustomAppName(e.target.value)} placeholder="e.g. Gorgias" className={inputClass + ' sm:flex-1'} data-id="element-1245" />
              <input type="number" value={customAppCost} onChange={e => setCustomAppCost(e.target.value)} placeholder="USD/mo" className={inputClass + ' sm:w-[140px]'} data-id="element-1246" />
              <button onClick={addCustomApp} disabled={!customAppName.trim() || customAppCost === ''} className="bg-[#2C2416] text-[#FAF7F2] px-6 py-3 rounded-full font-medium tracking-[0.5px] hover:scale-105 transition-all duration-300 hover:bg-[#5F6F52] disabled:opacity-40 disabled:hover:scale-100 disabled:hover:bg-[#2C2416] flex items-center justify-center gap-2" data-id="element-1247">
                <PlusIcon className="w-4 h-4" data-id="element-1248" />Add
              </button>
            </div>
          </div>
        </div>
      </motion.section>

      {/* SECTION 5: Multi-column comparison */}
      {columns.length > 0 && <motion.section {...fadeIn} className="py-12 px-6 md:px-10 bg-white" data-id="element-1249">
          <div className="max-w-[1400px] mx-auto" data-id="element-1250">
            <h2 className="text-[#2C2416] text-[clamp(28px,4vw,40px)] font-light tracking-[-2px] mb-2" data-id="element-1251">Platform cost comparison</h2>
            <p className="text-[#8B7355] text-lg mb-8" data-id="element-1252">Each Shopify wine club app gets its own column so you can compare true total cost.</p>
            <div className="overflow-x-auto overflow-y-visible" data-id="element-1253">
              <div style={{
            minWidth: `${200 + colCount * 180}px`
          }} data-id="element-1254">
                {/* Column headers */}
                <div className="grid gap-2 mb-1 px-0 mt-4" style={gridStyle} data-id="element-1255">
                  <div data-id="element-1256" />
                  {columns.map(col => {
                const isLowest = col.total === minTotal && col.total > 0 && c7Plan !== 'enterprise';
                return <div key={col.id} className={`text-center rounded-t-xl px-3 pt-6 pb-3 relative ${isLowest ? 'bg-[#5F6F52] text-white' : 'bg-[#FAF7F2]'}`} data-id="element-1257">
                        {isLowest && <div className="absolute -top-3 left-1/2 -translate-x-1/2 flex items-center gap-1 bg-[#5F6F52] text-white text-xs font-semibold px-3 py-1 rounded-full whitespace-nowrap" data-id="element-1258">
                            <CheckCircleIcon className="w-3 h-3" data-id="element-1259" />Lowest cost
                          </div>}
                        <p className={`text-xs font-semibold tracking-[1px] uppercase ${isLowest ? 'text-white/70' : 'text-[#8B7355]'}`} data-id="element-1260">{col.header}</p>
                        <p className={`text-sm font-medium mt-0.5 leading-tight ${isLowest ? 'text-white' : 'text-[#2C2416]'}`} data-id="element-1261">{col.subheader}</p>
                      </div>;
              })}
                </div>

                <div className="bg-[#FAF7F2] rounded-xl border border-[#D4A57420] overflow-hidden" data-id="element-1262">
                  {/* Platform */}
                  <div className="grid gap-2 px-4 py-3 bg-white border-b border-[#D4A57420]" style={gridStyle} data-id="element-1263">
                    <span className="text-[#8B7355] text-xs font-semibold tracking-[1.5px] uppercase self-center" data-id="element-1264">PLATFORM</span>
                    {columns.map(col => <div key={col.id} data-id="element-1265" />)}
                  </div>
                  <div className="grid gap-2 px-4 py-3 border-b border-[#D4A57410]" style={gridStyle} data-id="element-1266">
                    <span className="text-[#2C2416] text-sm self-center" data-id="element-1267">Platform subscription</span>
                    {columns.map(col => <div key={col.id} className="text-right self-center" data-id="element-1268">
                        <span className="text-[#2C2416] text-sm font-medium" data-id="element-1269">
                          {col.isC7 ? c7Plan === 'enterprise' ? 'Custom' : hasC7 && c7Config ? fmt(c7Config.priceAUD) : '—' : shopifyPlan === 'plus' ? 'From AUD $3,700/mo' : fmt(shopifyPlatformCostAUD)}
                        </span>
                      </div>)}
                  </div>

                  {/* Transaction fees */}
                  <div className="grid gap-2 px-4 py-3 bg-white border-t border-b border-[#D4A57420]" style={gridStyle} data-id="element-1270">
                    <span className="text-[#8B7355] text-xs font-semibold tracking-[1.5px] uppercase self-center" data-id="element-1271">TRANSACTION FEES</span>
                    {columns.map(col => <div key={col.id} data-id="element-1272" />)}
                  </div>
                  <div className="grid gap-2 px-4 py-3 border-b border-[#D4A57410]" style={gridStyle} data-id="element-1273">
                    <span className="text-[#2C2416] text-sm self-center" data-id="element-1274">Online transaction fees</span>
                    {columns.map(col => <div key={col.id} className="text-right self-center" data-id="element-1275">
                        <span className="text-[#2C2416] text-sm font-medium" data-id="element-1276">
                          {col.isC7 ? hasTxInputs && hasC7 && c7Config ? fmt(Math.round(c7TxSectionFee + c7StripeSectionFee)) : 'Enter volume above' : hasTxInputs && hasShopify && shopifyConfig ? fmt(Math.round(shopifyTxSectionFee)) : 'Enter volume above'}
                        </span>
                      </div>)}
                  </div>
                  <div className="grid gap-2 px-4 py-3 border-b border-[#D4A57410] bg-[#FAF7F2]/50" style={gridStyle} data-id="element-1277">
                    <span className="text-[#8B7355] text-sm pl-3 self-center" data-id="element-1278">Breakdown</span>
                    {columns.map(col => <div key={col.id} className="text-right self-center" data-id="element-1279">
                        <span className="text-[#8B7355] text-xs" data-id="element-1280">
                          {col.isC7 ? hasC7 && c7Config ? `${c7Config.txFee}% C7 fee + 1.7% Stripe + 30¢/tx` : '—' : hasShopify && shopifyConfig && shopifyConfig.value !== 'plus' ? `${shopifyConfig.ccRate}% + 30¢/tx via Shopify Payments` : shopifyConfig?.value === 'plus' ? 'Custom rates' : '—'}
                        </span>
                      </div>)}
                  </div>

                  {/* POS */}
                  {posPro && <>
                      <div className="grid gap-2 px-4 py-3 bg-white border-t border-b border-[#D4A57420]" style={gridStyle} data-id="element-1281">
                        <span className="text-[#8B7355] text-xs font-semibold tracking-[1.5px] uppercase self-center" data-id="element-1282">POS</span>
                        {columns.map(col => <div key={col.id} data-id="element-1283" />)}
                      </div>
                      <div className="grid gap-2 px-4 py-3 border-b border-[#D4A57410]" style={gridStyle} data-id="element-1284">
                        <div data-id="element-1285"><span className="text-[#2C2416] text-sm" data-id="element-1286">POS Pro × {posLocations} location{posLocations > 1 ? 's' : ''}</span><span className="block text-[#8B7355] text-xs italic" data-id="element-1287">C7 is all-in-one</span></div>
                        {columns.map(col => <div key={col.id} className="text-right self-center" data-id="element-1288">
                            <span className={`text-sm font-medium ${col.isC7 ? 'text-[#8B7355]' : 'text-[#2C2416]'}`} data-id="element-1289">{col.isC7 ? 'N/A' : fmt(posCostAUD)}</span>
                          </div>)}
                      </div>
                      {hasPosInputs && posTxFeeAUD > 0 && <div className="grid gap-2 px-4 py-3 border-b border-[#D4A57410]" style={gridStyle} data-id="element-1290">
                          <div data-id="element-1291"><span className="text-[#2C2416] text-sm" data-id="element-1292">In-person payment fee</span><span className="block text-[#8B7355] text-xs italic" data-id="element-1293">{posTxFeeLabel}</span></div>
                          {columns.map(col => <div key={col.id} className="text-right self-center" data-id="element-1294">
                              <span className={`text-sm font-medium ${col.isC7 ? 'text-[#8B7355]' : 'text-[#2C2416]'}`} data-id="element-1295">{col.isC7 ? 'N/A' : fmt(Math.round(posTxFeeAUD))}</span>
                            </div>)}
                        </div>}
                    </>}

                  {/* Wine club apps */}
                  {columns.some(c => !c.isC7 && c.appConfig) && <>
                      <div className="grid gap-2 px-4 py-3 bg-white border-t border-b border-[#D4A57420]" style={gridStyle} data-id="element-1296">
                        <span className="text-[#8B7355] text-xs font-semibold tracking-[1.5px] uppercase self-center" data-id="element-1297">WINE CLUB APP</span>
                        {columns.map(col => <div key={col.id} data-id="element-1298" />)}
                      </div>
                      <div className="grid gap-2 px-4 py-3 border-b border-[#D4A57410]" style={gridStyle} data-id="element-1299">
                        <div data-id="element-1300"><span className="text-[#2C2416] text-sm" data-id="element-1301">App flat fee</span><span className="block text-[#8B7355] text-xs italic" data-id="element-1302">C7 is all-in-one</span></div>
                        {columns.map(col => <div key={col.id} className="text-right self-center" data-id="element-1303">
                            {col.isC7 ? <span className="text-[#8B7355] text-sm font-medium" data-id="element-1304">N/A</span> : col.appConfig && !col.appConfig.custom ? <span className="text-[#2C2416] text-sm font-medium" data-id="element-1305">{fmtUSD(col.appConfig.priceUSD)}</span> : <span className="text-[#8B7355] text-sm" data-id="element-1306">—</span>}
                          </div>)}
                      </div>
                      <div className="grid gap-2 px-4 py-3 border-b border-[#D4A57410] bg-[#FAF7F2]/50" style={gridStyle} data-id="element-1307">
                        <div data-id="element-1308">
                          <span className="text-[#8B7355] text-sm pl-3" data-id="element-1309">Revenue fee</span>
                          <span className="block text-[#8B7355] text-xs italic pl-3" data-id="element-1310">{hasSubBilling ? `AUD ${totalSubBillingAUD.toLocaleString()}/mo ≈ USD ${Math.round(totalSubBillingUSD).toLocaleString()}/mo` : 'Enter sub billing above'}</span>
                        </div>
                        {columns.map(col => <div key={col.id} className="text-right self-center" data-id="element-1311">
                            {col.isC7 ? <span className="text-[#8B7355] text-sm font-medium" data-id="element-1312">N/A</span> : col.appConfig && !col.appConfig.custom && col.appRevFeeAUD !== undefined ? <span className="text-[#2C2416] text-sm font-medium" data-id="element-1313">{hasSubBilling ? col.appRevFeeAUD === 0 ? 'AUD $0/mo' : fmt(col.appRevFeeAUD) : '—'}</span> : <span className="text-[#8B7355] text-sm" data-id="element-1314">—</span>}
                          </div>)}
                      </div>
                    </>}

                  {/* Shared apps */}
                  {(Number(klaviyoContacts) > 0 || customApps.length > 0) && <>
                      <div className="grid gap-2 px-4 py-3 bg-white border-t border-b border-[#D4A57420]" style={gridStyle} data-id="element-1315">
                        <span className="text-[#8B7355] text-xs font-semibold tracking-[1.5px] uppercase self-center" data-id="element-1316">SHARED APPS</span>
                        {columns.map(col => <div key={col.id} data-id="element-1317" />)}
                      </div>
                      {Number(klaviyoContacts) > 0 && <div className="grid gap-2 px-4 py-3 border-b border-[#D4A57410]" style={gridStyle} data-id="element-1318">
                          <div data-id="element-1319"><span className="text-[#2C2416] text-sm" data-id="element-1320">Klaviyo</span><span className="block text-[#8B7355] text-xs italic" data-id="element-1321">Same on all</span></div>
                          {columns.map(col => <div key={col.id} className="text-right self-center" data-id="element-1322">
                              <span className="text-[#2C2416] text-sm font-medium" data-id="element-1323">{klaviyoNeedsContact ? 'Contact Klaviyo' : kCostUSD === 0 ? 'Free' : `USD $${kCostUSD}/mo\n(~AUD $${klaviyoCostAUD}/mo)`}</span>
                            </div>)}
                        </div>}
                      {customApps.map(app => <div key={app.id} className="grid gap-2 px-4 py-3 border-b border-[#D4A57410]" style={gridStyle} data-id="element-1324">
                          <div data-id="element-1325"><span className="text-[#2C2416] text-sm" data-id="element-1326">{app.name}</span><span className="block text-[#8B7355] text-xs italic" data-id="element-1327">Same on all</span></div>
                          {columns.map(col => <div key={col.id} className="text-right self-center" data-id="element-1328"><span className="text-[#2C2416] text-sm font-medium" data-id="element-1329">USD ${app.costUSD}/mo (~AUD ${Math.round(app.costUSD * usdToAud)}/mo)</span></div>)}
                        </div>)}
                    </>}

                  {/* Totals */}
                  <div className="grid gap-2 px-4 py-6 bg-white border-t-2 border-[#D4A57420]" style={gridStyle} data-id="element-1330">
                    <div data-id="element-1331">
                      <span className="text-[#2C2416] font-semibold text-lg block" data-id="element-1332">Estimated total</span>
                      {hasSubBilling && <span className="text-[#8B7355] text-xs" data-id="element-1333">Incl. platform + apps + revenue fees</span>}
                    </div>
                    {columns.map(col => {
                  const isLowest = col.total === minTotal && col.total > 0 && c7Plan !== 'enterprise';
                  const showTotal = !col.isC7 || c7Plan !== 'enterprise';
                  return <div key={col.id} className="text-right self-center" data-id="element-1334">
                          {showTotal ? <span className={`font-semibold text-xl ${isLowest ? 'text-[#5F6F52]' : 'text-[#2C2416]'}`} data-id="element-1335">AUD ${col.total.toLocaleString()}/mo</span> : <span className="text-[#8B7355] text-sm" data-id="element-1336">Custom</span>}
                        </div>;
                })}
                  </div>

                  {/* Savings callout */}
                  {columns.length > 1 && c7Plan !== 'enterprise' && (() => {
                const shopifyCols = columns.filter(c => !c.isC7);
                const c7Col = columns.find(c => c.isC7);
                if (!c7Col || shopifyCols.length === 0) return null;
                const cheapestShopify = Math.min(...shopifyCols.map(c => c.total));
                const savings = cheapestShopify - c7Col.total;
                if (Math.abs(savings) < 1) return null;
                return <div className="px-4 pb-5" data-id="element-1337">
                        <div className={`p-4 rounded-lg text-sm font-medium ${savings > 0 ? 'bg-[#5F6F52]/10 text-[#5F6F52]' : 'bg-[#8B7355]/10 text-[#8B7355]'}`} data-id="element-1338">
                          {savings > 0 ? `Commerce7 is AUD $${savings.toLocaleString()}/mo cheaper than the lowest-cost Shopify option.` : `The cheapest Shopify option is AUD $${Math.abs(savings).toLocaleString()}/mo cheaper than Commerce7.`}
                        </div>
                      </div>;
              })()}
                </div>

                <p className="text-[#8B7355] text-sm mt-6 leading-relaxed" data-id="element-1339">
                  Indicative only. Transaction fees included when online volume is entered above. App revenue fees estimated from subscription billing entered above. Contact us for a detailed review.
                </p>
              </div>
            </div>
          </div>
        </motion.section>}

      <Footer data-id="element-1340" />
    </div>;
}